/* ===========================================================================
 *  Waar ben ik? Alleen in de browser, voor "dichtbij"
 *
 *  De plek gaat niet naar onze server en wordt nergens bewaard
 *  (spec/rules.json: locatieWordtNietOpgeslagen).
 *
 *  Twee pogingen, omdat vooral iOS bij de eerste vraag geregeld "positie
 *  onbekend" of een time-out geeft terwijl het een tel later wel lukt:
 *
 *    1. snel: een plek van de laatste 10 minuten is goed genoeg
 *    2. lukt dat niet (behalve bij een weigering): watchPosition en de
 *       eerste plek die binnenkomt, met hoge nauwkeurigheid, hooguit 12 s
 * =========================================================================== */

export interface Plek { lat: number; lon: number }

export type LocatieFout = 'geweigerd' | 'onbekend' | 'te-lang' | 'niet-mogelijk'

function eenKeer(opties: PositionOptions): Promise<Plek> {
  return new Promise((goed, fout) => {
    navigator.geolocation.getCurrentPosition(
      (p) => goed({ lat: p.coords.latitude, lon: p.coords.longitude }), fout, opties)
  })
}

function eerstePlek(ms: number): Promise<Plek> {
  return new Promise((goed, fout) => {
    let klaar = false
    const stop = (id: number) => { klaar = true; navigator.geolocation.clearWatch(id) }
    const id = navigator.geolocation.watchPosition(
      (p) => { if (!klaar) { stop(id); goed({ lat: p.coords.latitude, lon: p.coords.longitude }) } },
      (e) => {
        /* Een tijdelijke "onbekend" tijdens het kijken: gewoon doorkijken. */
        if (e.code === e.PERMISSION_DENIED && !klaar) { stop(id); fout(e) }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: ms })
    setTimeout(() => { if (!klaar) { stop(id); fout({ code: 3 } as GeolocationPositionError) } }, ms)
  })
}

export async function bepaalPlek(): Promise<Plek> {
  if (!('geolocation' in navigator) || !window.isSecureContext) throw 'niet-mogelijk' satisfies LocatieFout
  try {
    return await eenKeer({ enableHighAccuracy: false, maximumAge: 600_000, timeout: 6_000 })
  } catch (e) {
    if ((e as GeolocationPositionError).code === 1) throw 'geweigerd' satisfies LocatieFout
  }
  try {
    return await eerstePlek(12_000)
  } catch (e) {
    const code = (e as GeolocationPositionError).code
    throw (code === 1 ? 'geweigerd' : code === 3 ? 'te-lang' : 'onbekend') satisfies LocatieFout
  }
}

const isApple = () => /iPhone|iPad|iPod/.test(navigator.userAgent)
  || (navigator.userAgent.includes('Macintosh') && 'ontouchend' in document)
const isAndroid = () => /Android/.test(navigator.userAgent)
const isWindows = () => /Windows/.test(navigator.userAgent)

/** Wat de klant kan doen, voor zijn eigen toestel. */
export function uitleg(f: LocatieFout): string {
  const of = ' Of typ hieronder je plaats of postcode.'
  if (f === 'niet-mogelijk') return 'Deze browser kan geen locatie delen.' + of
  if (f === 'geweigerd') {
    if (isApple()) return 'Je iPhone of iPad geeft de browser geen locatie. Zet aan: Instellingen > Privacy en beveiliging > Locatievoorzieningen (aan) > Safari-websites (of Chrome) > "Bij gebruik van de app". In Safari kan het ook via aA in de adresbalk > Website-instellingen > Locatie > Sta toe.' + of
    if (isAndroid()) return 'Je telefoon geeft de browser geen locatie. Tik op het slotje naast het adres > Rechten > Locatie > Toestaan, en kijk of Locatie aan staat in het snelmenu.' + of
    return 'Je browser geeft deze site geen locatie. Klik op het slotje links van het adres, zet Locatie op Toestaan en probeer opnieuw.' + of
  }
  if (f === 'te-lang') return 'Het bepalen van je locatie duurde te lang. Probeer het nog eens (buiten of bij een raam gaat het sneller).' + of
  if (isApple()) return 'Je iPhone of iPad weet zijn locatie nu niet. Kijk of Locatievoorzieningen aan staat (Instellingen > Privacy en beveiliging) en probeer het nog eens.' + of
  if (isWindows()) return 'Je computer weet zijn locatie niet. Op Windows: Instellingen > Privacy en beveiliging > Locatie aanzetten, ook "apps toegang geven tot je locatie".' + of
  return 'Je toestel weet zijn locatie nu niet. Kijk of Locatie aan staat en probeer het nog eens.' + of
}
